import type { SiteLocale } from "@/locales/fr/site";

import { EApplianceEstimateCode } from "@/domains/site/site.types.ts";
import { ESiteSubOwnerStatusType } from "@/types/backoffice/dto/reference/ref-site-sub-owner-status-type.ts";
import {
  ESiteInstallationFlowStatus,
  ESiteInstallationFlowType,
} from "@/types/backoffice/dto/site/site-installation-flow-dto.ts";
import { ESiteSearchField, ESiteSearchFilterType } from "@/types/backoffice/dto/site/site-search-dto.ts";

const siteLocale: SiteLocale = {
  site: {
    addressQuickSuggestions: "Quick Suggestions",
    updatePostalAndGeolocalizedAddress: "Update postal address and geolocalized address",
    useGeolocalizedAddressAsPostalAddress: "Use geolocalized address as postal address",
    updateAddress: "Update address",
    updateBookedAddress: "The site you want to change the address for already has a scheduled appointment!",
    updateBookedAddressWarning:
      "The appointment has been confirmed with the previous address. If you have changed the postal code or city, it is necessary to reschedule the appointment.",
    updateBookedAddressValidate: "I understand perfectly",
    addressNotFound: "Address not found ?",
    exactAddressFound: "Exact address found ?",
    geolocalizedAddress: "Geolocalized address",
    postalAddress: "Postal Address",
    nearAddress: "Nearest geolocalized address",
    incompleteAddress: "Incomplete address",
    differentNearAddress: "Different nearest geolocalized address",
    noGeolocalizedAddress: "No geolocalized address is available",
    showNearAddress: "Show nearest geolocalized address",
    hideNearAddress: "Hide nearest geolocalized address",
    searchTitle: "Search sites",
    searchSiteModalLabel: "Search by site ID, subscriber ID, name, phone or mail...",
    reachedMaxInterventionLimitWarning:
      "You have reached the limit of opened folders. Select a site in the list and then close call",
    installationFlow: {
      flow: {
        [ESiteInstallationFlowType.SALE]: "Sale",
        [ESiteInstallationFlowType.PROVISION]: "Provision",
      },
      status: {
        [ESiteInstallationFlowStatus.DONE]: "Done",
        [ESiteInstallationFlowStatus.ONGOING]: "Ongoing",
      },
    },
    updateOperation: "Update site operation",
    updateSite: "Update site",
    confirmRotation: "Confirm rotation",
    sendSensors: "Send sensors",
    sendGateway: "Send a gateway",
    site: "Site",
    edit: "Edit site",
    siteId: "Site ID",
    noRefMission: "No phoning mission",
    siteLayout: "Site subtype",
    siteLayouts: {
      siteLayoutStudio: "Studio",
      siteLayoutOneBedroom: "1 bedroom",
      siteLayoutTwoBedroom: "2 bedroom",
      siteLayoutThreeOrMoreBedroom: "3+ bedroom",
      siteLayoutNotSpecified: "Not Specified",
      siteLayoutFactoryOrNonStandardEquipment: "Factory / non-standard equipment",
      siteLayoutMunicipalBuildings: "Municipal buildings",
      siteLayoutOther: "Other",
      siteLayoutEducation: "Education",
      siteLayoutHolidayAccommodation: "Holiday accommodation",
      siteLayoutResidentialAccommodation: "Residential accommodation",
      siteLayoutHousingCollectiveInstallation: "Housing: collective installation",
      siteLayoutHotel: "Hotel",
      siteLayoutRestaurant: "Restaurant",
      siteLayoutFoodRetail: "Food retail",
      siteLayoutGeneralRetail: "General retail",
      siteLayoutAgency: "Agency",
      siteLayoutOffice: "Office",
    },
    siteTypeLayout: "Site type",
    siteTypeLayouts: {
      siteTypeDwellingHouse: "Dwelling - House/Bungalow",
      siteTypeDwellingApartment: "Dwelling - Apartment",
      siteTypeProfessional: "Professional installation",
      siteTypeDwellingNotSpecified: "Dwelling - Not specified",
      siteTypeAccommodationHouse: "Accommodation - House/Bungalow",
      siteTypeAccommodationApartment: "Accommodation - Apartment",
      siteTypeAccommodationNotSpecified: "Accommodation - Not specified",
    },
    create: "Create a site",
    search: "Search sites",
    siteType: "Site type",
    siteTypes: {
      residential: "Individual",
      tertiary: "Tertiary",
    },
    filterTypes: {
      [ESiteSearchFilterType.EQUALS]: "Equals...",
      [ESiteSearchFilterType.CONTAINS]: "Contains...",
      [ESiteSearchFilterType.STARTS_BY]: "Starts by...",
      [ESiteSearchFilterType.ENDS_BY]: "Ends by...",
    },
    fields: {
      [ESiteSearchField.SUBSCRIBER_ID]: "Subscriber ID",
      [ESiteSearchField.SUBSCRIBER_LASTNAME]: "Lastname",
      [ESiteSearchField.SUBSCRIBER_FIRSTNAME]: "Firstname",
      [ESiteSearchField.EMAIL]: "Email",
      [ESiteSearchField.PHONE]: "Phone number",
      [ESiteSearchField.COMPANY_NAME]: "Company name",
      [ESiteSearchField.SITE_ID]: "Site ID",
      [ESiteSearchField.EXTERN_ID]: "External ID",
      [ESiteSearchField.STREET]: "Address",
      [ESiteSearchField.POSTAL]: "Zip Code",
      [ESiteSearchField.TOWN]: "City",
      [ESiteSearchField.COUNTRY]: "Country",
      [ESiteSearchField.DELIVERY_POINT]: "MPAN",
      [ESiteSearchField.PILOT_HW_ID]: "Pilot - Hw ID",
      [ESiteSearchField.PILOT_CS_ID]: "Pilot - Cs ID",
      [ESiteSearchField.MOD_SERIAL]: "Modulator - Serial",
      [ESiteSearchField.MOD_HW_ID]: "Modulator - Hw ID",
      [ESiteSearchField.MOD_CS_ID]: "Modulator - Cs ID",
      [ESiteSearchField.MOD_NEURON_ID]: "Modulator - Neuron ID",
    },
    additionnalInfoCategory: "Additional information",
    technicalInfoCategory: "Technical information",
    siteTypeCategory: "Site type",
    partnerCategory: "Partnership",
    siteCoordinatesCategory: "Site coordinates",
    companyCategory: "Company information",
    generalCategory: "General information",
    externalId: "External identifier",
    companyName: "Business name",
    corporateName: "Company name",
    address: "Address",
    zipCode: "Zip Code",
    emptyAddressWarning: "No registered address was found, please set a geolocalized address",
    partnerAddressEmptyBtnLabel: "update subscriber's address",
    heatPumpNotEligibleWarning: "Heat pump model is not eligible",
    heatPumpToQualifyWarning:
      "Heat pump brand and model are missing: fill them in with “Update” under Appliances, otherwise the appointment will be cancelled.",
    heatPumpToFillPlaceholder: "To fill in",
    city: "City",
    addressComplement: "Address complement",
    digicode: "Access code",
    heatPumpBrand: "Heat pump brand",
    heatPumpModel: "Heat pump model",
    addOrDeleteOtherDeviceType: "Add/delete another device type (HPAC, ...)",
    stairway: "Stairway",
    level: "Floor",
    building: "Building",
    operation: "Operation",
    mission: "Mission",
    layout: "Site type",
    networkManager: "Distribution grid operator",
    subscribedPowerType: "Subscribed power",
    applianceEstimatesNumberInput: {
      [EApplianceEstimateCode.HEATER]: "Number of heater",
      [EApplianceEstimateCode.WATER_HEATER]: "Number of water heater",
      [EApplianceEstimateCode.HEAT_PUMP]: "Number of heat pump",
    },
    eligibleApplianceNumber: "Radiators and towel dryers",
    heatPumpInSite: "Is there a heat pump on the site?",
    occupancyType: "Occupancy type",
    occupancyTypes: {
      [ESiteSubOwnerStatusType.OTHER]: "Other",
      [ESiteSubOwnerStatusType.TENANT]: "Tenant",
      [ESiteSubOwnerStatusType.OWNER]: "Owner",
      [ESiteSubOwnerStatusType.UNDEFINED]: "Unknown",
    },
    surface: "Heated area (in m²)",
    buildingMoreThan2Years: "Residential building existing for more than 2 years?",
    wasEquippedWithTiko: "Former Tiko/Engie member – My Pilotage Elec?",
    fillYearMonthOldestHeater:
      "Please provide the approximate month and year of manufacture/installation of the oldest radiator",
    heaterInstallYear: "Year",
    heaterInstallMonth: "Month",
    heatingSystemOlderThan2Years: "Heating system existing for more than 2 years?",
    errorHeaterInstallDate:
      "The provided date is over 2 years old, so your response to the question is inconsistent. Please correct your answer or adjust the provided date",
    heatPumpDoesNotExistWarning: "This reference does not exist in our database, please add it",
    technicianInstructions: "Technician instructions",
    modifyTechnicianInstructions: "Modify instructions",
    noOperationWarningLabel: "Site has no operation attached, please ",
    noOperationWarningButton: "update site's operation",
    details: {
      mandatoryInstallation: "Mandatory installation",
    },
    deactivatedState: {
      withMyv: "Deactivated with MyV",
      withoutMyv: "Deactivated without MyV",
    },
  },
};

export default siteLocale;
